"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { Analytics, AnalyticsBrowser } from "@segment/analytics-next";
import { PostHog } from "posthog-js";
import { Campaign } from "@segment/analytics-core";
import { getOrCreateAnonymousId } from "@/utils/anonymousId";
import { SegmentAnalytics } from "posthog-js/lib/src/extensions/segment-integration";

declare global {
  interface Window {
    analytics: Analytics;
    $: JQueryStatic;
    jQuery: JQueryStatic;
    postHog: PostHog;
  }
}

interface AnalyticsContextProps {
  analytics: Analytics | undefined;
}

const AnalyticsContext = createContext<AnalyticsContextProps>({
  analytics: undefined,
});

export const useAnalytics = () => useContext(AnalyticsContext);

interface AnalyticsProviderProps {
  children: ReactNode;
  segmentWriteKey: string;
  postHogApiKey: string;
}

interface SegmentIdentifyOptions {
  anonymousId: string;
  context?: {
    campaign?: Campaign;
  };
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  segmentWriteKey,
  postHogApiKey,
}) => {
  const [analytics, setAnalytics] = useState<Analytics | undefined>(undefined);

  useEffect(() => {
    const loadAnalytics = async () => {
      const anonymousId = getOrCreateAnonymousId(); // Get the anonymousId

      const [analytics] = await AnalyticsBrowser.load({
        writeKey: segmentWriteKey,
      });

      analytics.ready(() => {
        const postHog = new PostHog();
        postHog.init(postHogApiKey, {
          api_host: "https://us.i.posthog.com",
          segment: analytics as unknown as SegmentAnalytics,
          autocapture: true,
          capture_pageview: false,
          capture_pageleave: true,
        });
        window.postHog = postHog;
      });

      analytics.setAnonymousId(anonymousId);

      // Add middleware to inject campaign context for every event
      await analytics.addSourceMiddleware(({ payload, next }) => {
        // Parse URL query parameters (if running in browser context)
        const params = new URLSearchParams(window.location.search);
        if (
          params.get("fbclid") ||
          params.get("gclid") ||
          params.get("utm_source")
        ) {
          const campaignData: Campaign = {
            name: params.get("utm_campaign") || "",
            source: params.get("utm_source") || "",
            medium: params.get("utm_medium") || "",
          };
          if (params.get("utm_term")) {
            campaignData.term = params.get("utm_term")!;
          }
          if (params.get("utm_content")) {
            campaignData.content = params.get("utm_content")!;
          }
          if (params.get("gclid")) {
            campaignData.gclid = params.get("gclid")!;
          }
          if (params.get("fbclid")) {
            campaignData.fbclid = params.get("fbclid")!;
          }

          // Merge campaign data into the event's context object
          payload.obj.context = {
            ...payload.obj.context, // preserve existing context (if any)
            campaign: campaignData, // add the campaign info under context.campaign
          };

          // And also save it into localStorage.
          localStorage.setItem("campaign", JSON.stringify(campaignData));
        }
        next(payload);
      });

      setAnalytics(analytics);

      // Make analytics instance globally available.
      window.analytics = analytics;

      // Dynamically load jQuery
      const script = document.createElement("script");
      script.src = "https://code.jquery.com/jquery-3.7.1.min.js";
      script.async = true;
      script.onload = () => {
        if (typeof window !== "undefined") {
          import("jquery").then((jqueryModule) => {
            const jquery = jqueryModule.default; // Get the default export
            window.$ = window.jQuery = jquery;

            // Attach the form submission listener
            window.$("form").on("submit", function (e: JQuery.SubmitEvent) {
              const form = new FormData(e.currentTarget);
              const data = Object.fromEntries(form.entries());
              if (data.email && data.anonymousId) {
                const identifyTraits = {
                  email: data.email as string,
                };

                const identifyOptions: SegmentIdentifyOptions = {
                  anonymousId: data.anonymousId as string,
                };

                // Check if the URL doesn't have campaign parameters.
                const params = new URLSearchParams(window.location.search);
                const hasCampaignQuery =
                  params.get("fbclid") ||
                  params.get("gclid") ||
                  params.get("utm_source");

                // If campaign parameters are missing, check localStorage.
                if (!hasCampaignQuery) {
                  const campaignDataString = localStorage.getItem("campaign");
                  if (campaignDataString) {
                    try {
                      const campaignData = JSON.parse(campaignDataString);
                      identifyOptions.context = {
                        ...(identifyOptions.context || {}),
                        campaign: campaignData,
                      };
                    } catch (error) {
                      console.error(
                        "Error parsing campaign data from localStorage",
                        error,
                      );
                    }
                  }
                }

                window.analytics.identify(identifyTraits, identifyOptions);
                // If we have the postHog object initialized, send the identify event there as well.
                if (window.postHog)
                  window.postHog.identify(data.email.toString(), {
                    email: data.email,
                  });
              }
            });
          });
        }
      };
      document.body.appendChild(script);
    };

    loadAnalytics();
  }, [segmentWriteKey, postHogApiKey]);

  return (
    <AnalyticsContext.Provider value={{ analytics }}>
      {children}
    </AnalyticsContext.Provider>
  );
};
